import React from "react";
import { Phone, ArrowDownRight, MapPin } from "lucide-react";
import { useT } from "@/i18n";

const Hero = () => {
  const { t } = useT();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain"
    >
      <div className="absolute inset-0">
        <img
          src="/generated/hero_concrete.png"
          alt="Exposed-aggregate decorative concrete surface"
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#faf7f2]/55 via-[#faf7f2]/80 to-[#faf7f2]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#faf7f2]/75 via-transparent to-[#faf7f2]/35" />
      </div>

      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-black/5 hidden lg:block" />
        <div className="absolute left-[16.66%] top-0 bottom-0 w-px bg-black/5 hidden lg:block" />
        <div className="absolute right-[16.66%] top-0 bottom-0 w-px bg-black/5 hidden lg:block" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 pt-40 pb-24 min-h-screen flex flex-col justify-between">
        <div className="flex items-center justify-between fade-up">
          <div className="flex items-center gap-3 text-zinc-600 text-xs tracking-[0.25em] uppercase">
            <MapPin className="w-3.5 h-3.5 text-[#ea7a1f]" />
            <span>{t.hero.pin}</span>
          </div>
          <div className="hidden sm:flex items-center gap-3 text-zinc-500 text-xs tracking-[0.2em] uppercase">
            <span className="text-[#ea7a1f]">●</span> {t.hero.stock}
          </div>
        </div>

        <div className="max-w-5xl fade-up" style={{ animationDelay: "0.15s" }}>
          <div className="overline mb-8">{t.hero.overline}</div>
          <h1 className="font-display text-[clamp(2.75rem,8vw,7.5rem)] leading-[0.92] tracking-[-0.03em] mb-10 text-[#14141a]">
            {t.hero.h1a}
            <br />
            {t.hero.h1b}
            <br />
            <span className="italic text-[#ea7a1f]">{t.hero.h1c}</span>
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <p className="md:col-span-6 text-lg text-zinc-700 leading-relaxed max-w-xl">
              <span className="text-[#ea7a1f] font-semibold">TOP-CAST®</span> {t.hero.sub}
            </p>

            <div className="md:col-span-6 flex flex-col sm:flex-row gap-4 md:justify-end">
              <a
                href="tel:5145941482"
                data-testid="hero-call-button"
                className="group inline-flex items-center justify-between gap-6 bg-[#ea7a1f] hover:bg-[#f08b34] text-white font-bold px-7 py-5 transition-all duration-300 hover:translate-y-[-3px] min-w-[280px]"
              >
                <span className="flex items-center gap-3">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                  </span>
                  <span className="tracking-wide">{t.hero.cta}</span>
                </span>
                <Phone className="w-5 h-5 group-hover:rotate-[15deg] transition-transform" strokeWidth={2.5} />
              </a>

              <a
                href="#product"
                data-testid="hero-explore-button"
                className="inline-flex items-center justify-between gap-3 border border-black/30 hover:border-[#14141a] text-[#14141a] px-6 py-5 transition-all"
              >
                <span className="tracking-wide">{t.hero.explore}</span>
                <ArrowDownRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10 border-y border-black/10 fade-up" style={{ animationDelay: "0.35s" }}>
          {t.hero.stats.map((s, i) => (
            <div key={i} className="bg-[#faf7f2] px-6 py-6">
              <div className="font-display text-3xl sm:text-4xl text-[#14141a] num-display">{s.v}</div>
              <div className="text-xs tracking-[0.2em] uppercase text-zinc-500 mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
